import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Alert from '@mui/material/Alert';

import { useDeepCompareEffect, useDeepCompareMemo, useMountedEffect } from '@clatter/platform';
import {
  FolderManagerAction,
  FolderManagerActionTypes,
  FolderManagerBreadcrumb,
  FolderManagerDragStartPayload,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
  FolderManagerItemTypes,
  FolderManagerMimeTypes,
  FolderManagerViewTypes,
} from '../../interfaces';

import { useUpdateAssetMutation, useUpdateFolderMutation, useUpdatePresentationMutation } from '../../services';
import { ViewTypeSwitcher, GridZoomControls } from '../../shared';
import { getFolderManagerColumns, handleAssignItemsToFolder } from '../../helpers';
import { NgBreadcrumbs, NgCard, NgCardHeader, NgCardContent, IconButtonRounded, StickyHeader } from '../UI';
import { DetailsRail } from './Components/DetailsRail';
import { FolderManagerHeader } from './Components/FolderManagerHeader';
import { FolderManagerGridView } from './Components/FolderManagerGridView';
import { FolderManagerTableView } from './Components/FolderManagerTableView';
import {
  FolderManagerSlidesViewFilters,
  SlidesViewFilters,
  GROUP_BY_OPTIONS,
  SORT_BY_OPTIONS,
  SORT_DIRECTION_OPTIONS,
} from './Components/FolderManagerSlidesViewFilters';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import chevronIcon from '../../../assets/icons/chevron-light.svg';

//region INTERFACES

interface FolderManagerProps {
  isLoading: boolean;
  fileInputRef?: any;
  rootFolderId: number;
  noDataMessage?: string;
  selectedFolderId: any; // FIXME
  viewStateKey?: string;

  hierarchyData: FolderManagerItem[];
  breadcrumbs: FolderManagerBreadcrumb[];
  disableUpload?: boolean;
  defaultViewType?: FolderManagerViewTypes;
  newButtonActionType?: string | null; //@todo - remove after cleanup
  minHeight?: string;
  acceptedDragTypes?: FolderManagerMimeTypes[]; // Array of accepted MIME types for dragging
  stickyHeaderTop?: number; // Top offset for sticky header in pixels
  availableViews?: (FolderManagerViewTypes.SLIDES | FolderManagerViewTypes.GRID | FolderManagerViewTypes.LIST)[];
  enableZoomControls?: boolean;
  isSlideRepository?: boolean;
  showThemePicker?: boolean;

  onClearNewButtonAction: () => void;
  customMultiSelectActions?: (data: {
    selectedItems: any[];
    onClear: () => void;
    onAction?: FolderManagerAction;
  }) => void;
  onAction: (data: FolderManagerAction) => void;
  onSelect?: (data: { selectedRows: FolderManagerItem[]; clearSelection: () => void }) => void;
  onAddSlide?: (slide: any) => void; // Callback when adding a slide from expanded PPTX
  allowEntityClick?: boolean;
  disableActionColumn?: boolean;
  disableInternalDnD?: boolean;
}

//endregion

//region STYLED
const StyledMainContainer = styled.div<{ $detailsOpened?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  overflow: visible;

  @media (max-width: 860px) {
    flex-direction: column;
  }

  > div:first-child {
    flex: ${({ $detailsOpened }) => ($detailsOpened ? '0 0 70%' : '0 0 100%')};
    min-width: 0;
  }

  > div:nth-child(2) {
    flex: ${({ $detailsOpened }) => ($detailsOpened ? '1' : '0')};
  }
`;

const StyledZoomControlsWrapper = styled.div`
  align-self: flex-end;
`;

const StyledVerticalDivider = styled.div`
  width: 1px;
  height: 24px;
  background-color: ${({ theme }) => theme.palette.border};
`;

//endregion

const findItemRowIndex = ({ rows: rowItems, item }: { rows: FolderManagerItem[]; item: FolderManagerItem }) =>
  (rowItems || []).findIndex((row: FolderManagerItem) => row?.id === item.id && row.type === item.type);

export const FolderManager = ({
  isLoading,
  breadcrumbs,
  noDataMessage,
  hierarchyData = [],
  defaultViewType = FolderManagerViewTypes.GRID,
  minHeight,
  onAction,
  onSelect,
  onAddSlide,
  allowEntityClick = false,
  disableActionColumn = false,
  disableInternalDnD = false,
  disableUpload = false,
  acceptedDragTypes,
  stickyHeaderTop,
  viewStateKey,
  isSlideRepository,
  showThemePicker,
  availableViews = [FolderManagerViewTypes.GRID, FolderManagerViewTypes.LIST],
  enableZoomControls = false,
}: FolderManagerProps) => {
  const location = useLocation();
  const { slug } = useParams();

  const [updateFolder] = useUpdateFolderMutation();
  const [updateAsset] = useUpdateAssetMutation();
  const [updatePresentation] = useUpdatePresentationMutation();

  //region STATE
  const [detailsOpened, setDetailsOpened] = useState(false);
  const [activeView, setActiveView] = useState(defaultViewType);
  const [selectedItems, setSelectedItems] = useState<FolderManagerItem[]>([]);
  const [notifications, setNotifications] = useState<{ type: string; message: string }[]>([]);
  const [zoomValue, setZoomValue] = useState(40);
  const [zoomExpanded, setZoomExpanded] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [slidesFilters, setSlidesFilters] = useState<SlidesViewFilters>({
    dateRange: null,
    groupBy: GROUP_BY_OPTIONS.NONE,
    sortDirection: SORT_DIRECTION_OPTIONS.NEWEST,
    sortBy: SORT_BY_OPTIONS.MODIFIED,
  });

  const topBarRef = useRef<HTMLDivElement | null>(null);
  const notificationContainer = document.getElementById('notification-container');

  const mappedRows = useMemo(() => {
    return (hierarchyData || []).flatMap((item: FolderManagerItem, index: number) => {
      const getStatus = () => {
        // FIXME: item.type !== FolderManagerMimeTypes.PPTX - this is temporary only for demo
        if (
          item.type === FolderManagerMimeTypes.FOLDER ||
          item.type === FolderManagerMimeTypes.WORKSPACE ||
          item.type === FolderManagerItemTypes.PRESENTATION ||
          item.type !== FolderManagerMimeTypes.PPTX
        ) {
          return 'completed';
        }
        // return loadingStateItem && loadingStateItem.ready ? 'completed' : 'pending'
        return 'completed';
      };

      // in slide repo folders, flatten asset slides into individual items
      if (isSlideRepository && item.slides?.length) {
        return item.slides.map((slide: any, slideIndex: number) => ({
          ...slide,
          type: FolderManagerMimeTypes.SLIDE,
          id: `${(item as any)?.currentVersion?.ID}-${slideIndex}`,
          instanceId: `${(item as any)?.currentVersion?.ID}-${slideIndex}`,
          name: slide.displayName || `${item.name} - Slide ${slideIndex + 1}`,
          owner: (item as any).owner,
          createdBy: (item as any).createdBy,
          fileSize: (item as any).fileSize,
          createdAt: (item as any).createdAt,
          updatedAt: (item as any).updatedAt,
          itemData: { ...item, slideData: slide.itemData },
          actions: [{ name: FolderManagerItemActionNames.DOWNLOAD_DECK, type: FolderManagerItemActionTypes.SINGLE }],
          rowIndex: index,
          status: 'completed',
        }));
      }

      return [
        {
          ...item,
          rowIndex: index,
          status: getStatus(),
        },
      ];
    });
  }, [hierarchyData, isSlideRepository]);

  const columns = useDeepCompareMemo(() => {
    const columns = getFolderManagerColumns({ isLoading: isLoading, selectedItems: selectedItems, slug, onAction });

    if (disableActionColumn) {
      return columns.filter((c) => c.field !== 'action');
    }

    return columns;
  }, [disableActionColumn, selectedItems, isLoading]);

  const handleChangeView = useCallback((view: FolderManagerViewTypes) => {
    if (view === FolderManagerViewTypes.SLIDES) {
      setSelectedItems([]);
    }
    setActiveView(view);
  }, []);

  const handleCloseNotification = (index: number) => {
    setNotifications((prevNotifications) => {
      const updatedNotifications = [...prevNotifications];

      updatedNotifications.splice(index, 1);

      return updatedNotifications;
    });
  };

  const handleBreadcrumbClick = ({ item }: { item: any; index: number }) => {
    if (!slug) {
      onAction({ actionType: FolderManagerActionTypes.NAVIGATE, payload: item });
      return;
    }

    if (item?.slug) {
      onAction({ actionType: FolderManagerActionTypes.NAVIGATE, payload: item });
      return;
    }

    if (!item?.id) {
      console.log('ERROR:Folder Manager:: breadcrumb item id not provided');
      return;
    }

    onAction({ actionType: FolderManagerActionTypes.GET_SUB_FOLDERS, payload: { id: item.id } as FolderManagerItem });
  };

  const handleDragStart = (data: FolderManagerDragStartPayload) => {
    if (!data || !data?.id) {
      console.log('ERROR: invalid props');
      return;
    }

    if (!selectedItems || !selectedItems.length) {
      const selectedItem = mappedRows.find((row: any) => row.id === +data.id);

      if (!selectedItem) {
        console.error('Error, selected item data not found!');
        return;
      }

      setSelectedItems([selectedItem]);
    }
  };

  const handleDragEnd = async (payload: IFolderManagerDragEndEvent) => {
    if (slug === 'trash') {
      console.log('Trash drag actions are disabled');
      return;
    }

    // if target id folder
    if (payload.type === FolderManagerItemTypes.FOLDER) {
      const targetFolderData = mappedRows.find(
        (item: any) => item.id === Number(payload.id) && item.type === FolderManagerItemTypes.FOLDER,
      );

      if (!targetFolderData) {
        //@todo - notify user: error
        console.error('Error moving items to folder. Target folder data not found!');
        return;
      }

      const result = await handleAssignItemsToFolder({
        target: { id: targetFolderData.id, name: targetFolderData.name },
        selectedItems: selectedItems,
        updateFolderCb: updateFolder,
        updateAssetCb: updateAsset,
        updatePresentationCb: updatePresentation,
      });

      // @todo: notify user: success / errors
      // console.log('handleAssignItemsToFolder: result', result);

      setSelectedItems([]);
      return;
    }
  };

  const handleRowDoubleClick = ({ row }: { row: FolderManagerItem }) => {
    // handle folder click (allow navigation even in trash)
    if (row.type === FolderManagerMimeTypes.FOLDER) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.GET_SUB_FOLDERS, payload: row });
      return;
    }

    // Ignore trashed or deleted items for non-folder items
    if (row?.trashedAt || row?.deletedAt) {
      return;
    }

    if (row.type === FolderManagerMimeTypes.WORKSPACE) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.GET_WORKSPACE, payload: row });
      return;
    }

    // handle presentation double-click
    if (row.type === FolderManagerItemTypes.PRESENTATION) {
      setSelectedItems([]);
      onAction({ actionType: FolderManagerActionTypes.EDIT, payload: row });
      return;
    }

    // handle shortcut click
    if (row.sourceId) {
      if (!row?.source?.ID && !row?.source?.FolderID) {
        window.alert('Sorry original asset has been removed!');
        return;
      }

      setSelectedItems([]);
      onAction({
        actionType: FolderManagerActionTypes.GET_SUB_FOLDERS,
        payload: { id: row.source.FolderID } as FolderManagerItem,
      });
    }
  };

  const handleRowSelect = ({ event, item, visibleItems }: { event: any; item: any; visibleItems?: any[] }) => {
    event.preventDefault();

    if (allowEntityClick) {
      onAction({ actionType: FolderManagerActionTypes.ROW_CLICK, payload: { id: item.id } as FolderManagerItem });
      return;
    }

    const rows = visibleItems ?? mappedRows;

    setSelectedItems((prevSelectedItems) => {
      const selectedItem = prevSelectedItems[0]; // Access the currently selected item if it exists

      // Check for shift key and only one item selected
      if (event?.shiftKey && prevSelectedItems?.length === 1 && selectedItem?.id) {
        const startRowIndex = findItemRowIndex({ rows, item: selectedItem });
        const endRowIndex = findItemRowIndex({ rows, item: item });

        if (startRowIndex === -1) {
          console.log('ERROR: startRowIndex not found');
          return [];
        }

        const isExpanding = startRowIndex < endRowIndex;

        // Select the range of items between start and end indices
        return rows
          .slice(isExpanding ? startRowIndex : endRowIndex, isExpanding ? endRowIndex + 1 : startRowIndex + 1)
          .filter((row: any) => {
            const isFolder =
              row.type === FolderManagerMimeTypes.FOLDER || row.type === FolderManagerMimeTypes.WORKSPACE;
            const isTypeAccepted = !acceptedDragTypes || isFolder || acceptedDragTypes.includes(row.type);
            return isTypeAccepted && row.selectable !== false;
          });
      }
      // Clear selection if the clicked item is already selected
      else if (prevSelectedItems?.length === 1 && selectedItem.id === item.id && selectedItem.type === item.type) {
        return []; // Clear the selection
      }
      // Default case: select the clicked item
      else {
        return [item]; // Select the clicked item
      }
    });
  };

   // Function to clear selection (exposed to parent)
  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);
  //endregion

  //region EFFECTS
  // toggle active view
  useMountedEffect(() => {
    setActiveView(defaultViewType);
  }, [defaultViewType]);

  // clear selection and close details rail when navigating to a different folder
  useEffect(() => {
    setSelectedItems([]);
    setDetailsOpened(false);
  }, [slug]);

  // Notify parent when selection changes
  useDeepCompareEffect(() => {
    if (onSelect) {
      onSelect({ selectedRows: selectedItems, clearSelection });
    }
  }, [selectedItems, clearSelection]);

  return (
    <StyledMainContainer $detailsOpened={detailsOpened}>
      <NgCard>
        <NgCardContent minHeight={minHeight}>
          <StickyHeader top={stickyHeaderTop} noWrap={!stickyHeaderTop}>
            <NgCardHeader
              ref={topBarRef}
              hideFilters={!zoomExpanded && selectedItems.length <= 1}
              noBorder={!zoomExpanded && !filtersExpanded && selectedItems.length <= 1}
              filters={
                <FolderManagerHeader
                  filters={
                    <>
                      {enableZoomControls && activeView !== FolderManagerViewTypes.LIST && zoomExpanded && (
                        <StyledZoomControlsWrapper>
                          <GridZoomControls value={zoomValue} onChange={setZoomValue} />
                        </StyledZoomControlsWrapper>
                      )}
                    </>
                  }
                  onAction={onAction}
                  selectedItems={selectedItems}
                  onClear={() => setSelectedItems([])}
                  isLoading={isLoading}
                />
              }
            >
              <NgBreadcrumbs data={breadcrumbs} onClick={handleBreadcrumbClick} />

              <ViewTypeSwitcher activeView={activeView} onChange={handleChangeView} availableViews={availableViews} />

              {(enableZoomControls || isSlideRepository) && activeView !== FolderManagerViewTypes.LIST && (
                <StyledVerticalDivider />
              )}

              {enableZoomControls && activeView !== FolderManagerViewTypes.LIST && (
                <IconButtonRounded
                  active={zoomExpanded}
                  title={zoomExpanded ? 'Hide actions' : 'Show actions'}
                  icon={<TuneIcon style={{ fontSize: '16px' }} />}
                  materialIcon
                  onClick={() => setZoomExpanded((prev) => !prev)}
                />
              )}

              {isSlideRepository && activeView !== FolderManagerViewTypes.LIST && (
                <IconButtonRounded
                  active={filtersExpanded}
                  title={filtersExpanded ? 'Hide filters' : 'Show filters'}
                  icon={<FilterAltIcon style={{ fontSize: '16px' }} />}
                  onClick={() => setFiltersExpanded((prev) => !prev)}
                  materialIcon
                />
              )}

              <IconButtonRounded
                active={detailsOpened}
                title="Details"
                icon={<img src={chevronIcon} alt="Chevron" />}
                onClick={() => setDetailsOpened((prev) => !prev)}
                activeAccent
                flipIcon
              />
            </NgCardHeader>

            {isSlideRepository && activeView !== FolderManagerViewTypes.LIST && filtersExpanded && (
              <FolderManagerSlidesViewFilters
                filters={slidesFilters}
                viewStateKey={viewStateKey}
                showThemePicker={showThemePicker}
                onChange={setSlidesFilters}
              />
            )}
          </StickyHeader>

          <div>
            {activeView === FolderManagerViewTypes.LIST ? (
              <FolderManagerTableView
                items={mappedRows}
                columns={columns}
                noDataMessage={noDataMessage}
                selectedRows={selectedItems}
                isLoading={isLoading}
                onAction={onAction}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onSelect={handleRowSelect}
                onDoubleClick={handleRowDoubleClick}
                storageKey={location.pathname.substring(1)}
                disableInternalDnD={disableInternalDnD}
                disableUpload={disableUpload}
                acceptedDragTypes={acceptedDragTypes}
              />
            ) : (
              <FolderManagerGridView
                items={mappedRows}
                selectedRows={selectedItems}
                noDataMessage={noDataMessage}
                isLoading={isLoading}
                minHeight={minHeight}
                viewStateKey={viewStateKey}
                onAction={onAction}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onSelect={handleRowSelect}
                onDoubleClick={handleRowDoubleClick}
                disableInternalDnD={disableInternalDnD}
                disableUpload={disableUpload}
                acceptedDragTypes={acceptedDragTypes}
                slidesViewEnabled={isSlideRepository}
                showThemePicker={showThemePicker}
                onAddSlide={onAddSlide}
                zoomValue={enableZoomControls ? zoomValue : undefined}
                filters={isSlideRepository ? slidesFilters : undefined}
                onFiltersChange={isSlideRepository ? setSlidesFilters : undefined}
              />
            )}
          </div>

          {notifications.length > 0 &&
            ReactDOM.createPortal(
              <>
                {notifications.map((notification, index) => (
                  <Alert severity={notification.type as any} onClose={() => handleCloseNotification(index)}>
                    {notification.message}
                  </Alert>
                ))}
              </>,
              notificationContainer || document.body, // Use the specified container as the portal container
            )}
        </NgCardContent>
      </NgCard>

      {detailsOpened && (
        <DetailsRail
          selectedItems={selectedItems}
          onClose={() => setDetailsOpened(false)}
          onAction={onAction}
          stickyTop={stickyHeaderTop}
          viewStateKey={viewStateKey}
        />
      )}
    </StyledMainContainer>
  );
};
